// frontend/src/components/UI/PhoneNumberInput.js
import React, { useState, useEffect, useRef } from 'react';
import { Phone } from 'lucide-react';
import { parsePhoneNumber } from 'libphonenumber-js';

/**
 * PhoneNumberInput - Phone number input with country code dropdown
 * 
 * Features:
 * - Country code dropdown (defaults to Singapore +65)
 * - Flexible phone number input (supports all country formats)
 * - E.164 format output (e.g., +6590000000)
 * - Mobile-optimized touch targets
 * - Validation handled by parent component using libphonenumber-js
 * 
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.name - Field name for form handling
 * @param {string} props.value - Current phone value (E.164 format)
 * @param {Function} props.onChange - Change handler (receives {target: {name, value}})
 * @param {string} props.error - Error message to display
 * @param {boolean} props.required - Whether field is required
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.defaultCountry - Default country code (default: 'SG')
 * @param {boolean} props.disabled - Whether field is disabled
 */

const COUNTRIES = [
  { code: '+65', name: 'Singapore', value: 'SG' },
  { code: '+60', name: 'Malaysia', value: 'MY' },
  { code: '+91', name: 'India', value: 'IN' },
  { code: '+880', name: 'Bangladesh', value: 'BD' },
  { code: '+95', name: 'Myanmar', value: 'MM' },
  { code: '+63', name: 'Philippines', value: 'PH' },
  { code: '+86', name: 'China', value: 'CN' },
  { code: '+94', name: 'Sri Lanka', value: 'LK' },
];

const PhoneNumberInput = ({
  label,
  name,
  value,
  onChange,
  error,
  required = false,
  placeholder = "Enter phone number",
  defaultCountry = "SG",
  disabled = false
}) => {
  // Extract country code and phone number from value
  const getCountryFromValue = (phoneValue) => {
    if (!phoneValue) return defaultCountry;
    
    // Check if value starts with any predefined country code
    for (const country of COUNTRIES) {
      if (phoneValue.startsWith(country.code)) {
        return country.value;
      }
    }
    
    // If starts with + but doesn't match predefined countries, it's "Other"
    if (phoneValue.startsWith('+')) {
      return 'OTHER';
    }
    
    return defaultCountry;
  };

  const getPhoneNumberFromValue = (phoneValue) => {
    if (!phoneValue) return '';
    
    // Try to remove predefined country code prefix first
    for (const country of COUNTRIES) {
      if (phoneValue.startsWith(country.code)) {
        return phoneValue.substring(country.code.length).trim();
      }
    }
    
    // If starts with + but doesn't match predefined, extract custom code
    if (phoneValue.startsWith('+')) {
      // Match country code pattern (+ followed by 1-4 digits)
      const match = phoneValue.match(/^(\+\d{1,4})(.+)$/);
      if (match) {
        return match[2].trim(); // Return the phone number part
      }
    }
    
    // If no country code found, assume it's just the number
    return phoneValue;
  };

  // Extract custom country code from value if it doesn't match any predefined country
  const getCustomCountryCode = (phoneValue) => {
    if (!phoneValue || !phoneValue.startsWith('+')) {
      return '';
    }
    
    // Check if it matches any predefined country
    for (const country of COUNTRIES) {
      if (phoneValue.startsWith(country.code)) {
        return ''; // Matches a predefined country
      }
    }
    
    // If starts with + but doesn't match predefined, extract custom code
    const match = phoneValue.match(/^(\+\d{1,4})/);
    return match ? match[1] : '';
  };

  const initialCountry = getCountryFromValue(value);
  const initialCustomCode = initialCountry === 'OTHER' ? getCustomCountryCode(value) : '';
  const initialPhoneNumber = getPhoneNumberFromValue(value);

  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [customCountryCode, setCustomCountryCode] = useState(initialCustomCode);
  const [validationState, setValidationState] = useState({
    isValid: null,
    isWarning: false,
    message: null,
    digitCount: 0,
    maxDigits: null,
    countryName: null
  });
  const debounceTimerRef = useRef(null);

  // Update local state when value prop changes (e.g., when editing)
  useEffect(() => {
    const country = getCountryFromValue(value);
    const number = getPhoneNumberFromValue(value);
    const customCode = getCustomCountryCode(value);
    
    // Check if value has a custom country code
    if (customCode && !COUNTRIES.some(c => value.startsWith(c.code))) {
      setSelectedCountry('OTHER');
      setCustomCountryCode(customCode);
      setPhoneNumber(value.substring(customCode.length).trim());
    } else {
      setSelectedCountry(country);
      setPhoneNumber(number);
      setCustomCountryCode('');
    }
  }, [value]);

  // Real-time phone number validation with debouncing
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Skip validation for empty or very short inputs (< 3 digits)
    if (!phoneNumber || phoneNumber.length < 3) {
      setValidationState({
        isValid: null,
        isWarning: false,
        message: null,
        digitCount: phoneNumber?.length || 0,
        maxDigits: null,
        countryName: null
      });
      return;
    }

    // Debounce validation (300ms delay)
    debounceTimerRef.current = setTimeout(() => {
      let countryCode = '';
      let countryValue = selectedCountry;
      
      if (selectedCountry === 'OTHER') {
        countryCode = customCountryCode || '';
        // For "Other" country, skip length validation, only check format
        if (!countryCode) {
          setValidationState({
            isValid: null,
            isWarning: false,
            message: null,
            digitCount: phoneNumber.length,
            maxDigits: null,
            countryName: 'Other'
          });
          return;
        }
      } else {
        const countryData = COUNTRIES.find(c => c.value === selectedCountry) || COUNTRIES[0];
        countryCode = countryData.code;
      }

      const fullNumber = countryCode ? `${countryCode}${phoneNumber}` : phoneNumber;

      // Skip validation if number doesn't start with +
      if (!fullNumber.startsWith('+')) {
        setValidationState({
          isValid: null,
          isWarning: false,
          message: null,
          digitCount: phoneNumber.length,
          maxDigits: null,
          countryName: null
        });
        return;
      }

      try {
        // Parse phone number to get country-specific metadata
        const parsed = parsePhoneNumber(fullNumber);
        
        if (parsed && parsed.country) {
          const countryName = COUNTRIES.find(c => c.value === parsed.country)?.name || parsed.country;
          
          // Get expected length for the country
          // libphonenumber-js provides nationalNumber which is the number without country code
          const nationalNumber = parsed.nationalNumber;
          const digitCount = nationalNumber.length;
          
          // Use known country-specific lengths (based on ITU-T E.164 recommendations)
          const countryLengths = {
            'SG': 8,  // Singapore: 8 digits (mobile and landline)
            'MY': { min: 9, max: 10 },  // Malaysia: 9-10 digits (mobile: 9-10, landline: 9)
            'IN': 10, // India: 10 digits
            'BD': 10, // Bangladesh: 10 digits
            'MM': { min: 8, max: 9 },  // Myanmar: 8-9 digits
            'PH': 10, // Philippines: 10 digits
            'CN': 11, // China: 11 digits
            'LK': 9   // Sri Lanka: 9 digits
          };
          
          let maxDigits = null;
          const countryLength = countryLengths[parsed.country];
          
          if (typeof countryLength === 'number') {
            maxDigits = countryLength;
          } else if (typeof countryLength === 'object' && countryLength.max) {
            maxDigits = countryLength.max;
          } else {
            // For unknown countries, try to infer from parsed number metadata
            // Most countries have 7-15 digits total (including country code)
            // National number is typically 4-12 digits
            // We'll use a conservative estimate
            maxDigits = null; // Don't validate length for unknown countries
          }

          // Determine validation state
          let isValid = null;
          let isWarning = false;
          let message = null;

          if (maxDigits !== null) {
            if (digitCount > maxDigits) {
              isValid = false;
              isWarning = false;
              message = `Too many digits. ${countryName} numbers must be ${maxDigits} digits (you entered ${digitCount}).`;
            } else if (digitCount === maxDigits) {
              isValid = true;
              isWarning = false;
              message = `${digitCount}/${maxDigits} digits`;
            } else if (digitCount >= maxDigits - 1) {
              isValid = null;
              isWarning = true;
              message = `${digitCount}/${maxDigits} digits`;
            } else {
              isValid = null;
              isWarning = false;
              message = `${digitCount}/${maxDigits} digits`;
            }
          } else {
            // For countries without known length, just validate format
            isValid = parsed.isValid() ? true : null;
            isWarning = false;
            message = `${digitCount} digits`;
          }

          setValidationState({
            isValid,
            isWarning,
            message,
            digitCount,
            maxDigits,
            countryName
          });
        } else {
          // Invalid country or format
          setValidationState({
            isValid: false,
            isWarning: false,
            message: 'Invalid phone number format',
            digitCount: phoneNumber.length,
            maxDigits: null,
            countryName: null
          });
        }
      } catch (error) {
        // Parse error - invalid format
        setValidationState({
          isValid: false,
          isWarning: false,
          message: 'Invalid phone number format',
          digitCount: phoneNumber.length,
          maxDigits: null,
          countryName: null
        });
      }
    }, 300);

    // Cleanup timer on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [phoneNumber, selectedCountry, customCountryCode]);

  const selectedCountryData = selectedCountry === 'OTHER' 
    ? { code: customCountryCode || '+', name: 'Other', value: 'OTHER' }
    : (COUNTRIES.find(c => c.value === selectedCountry) || COUNTRIES[0]);

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setSelectedCountry(newCountry);
    
    if (newCountry === 'OTHER') {
      // If switching to Other, clear the phone number until custom code is entered
      setCustomCountryCode('');
      onChange({
        target: {
          name,
          value: ''
        }
      });
    } else {
      // Combine new country code with existing phone number
      const countryData = COUNTRIES.find(c => c.value === newCountry) || COUNTRIES[0];
      const fullNumber = phoneNumber ? `${countryData.code}${phoneNumber}` : '';
      
      onChange({
        target: {
          name,
          value: fullNumber
        }
      });
    }
  };

  const handleCustomCountryCodeChange = (e) => {
    const newCode = e.target.value.replace(/[^+\d]/g, ''); // Only allow + and digits
    // Ensure it starts with +
    const formattedCode = newCode.startsWith('+') ? newCode : `+${newCode}`;
    setCustomCountryCode(formattedCode);
    
    // Combine custom country code with phone number
    const fullNumber = phoneNumber && formattedCode ? `${formattedCode}${phoneNumber}` : '';
    
    onChange({
      target: {
        name,
        value: fullNumber
      }
    });
  };

  const handlePhoneChange = (e) => {
    const newNumber = e.target.value.replace(/\s/g, ''); // Remove spaces
    setPhoneNumber(newNumber);
    
    // Combine country code with phone number
    let countryCode = '';
    if (selectedCountry === 'OTHER') {
      countryCode = customCountryCode || '';
    } else {
      const countryData = COUNTRIES.find(c => c.value === selectedCountry) || COUNTRIES[0];
      countryCode = countryData.code;
    }
    
    const fullNumber = newNumber && countryCode ? `${countryCode}${newNumber}` : '';
    
    onChange({
      target: {
        name,
        value: fullNumber
      }
    });
  };

  // Ensure error is always a string, never an object
  const safeError = error && typeof error === 'string' ? error : (error ? String(error) : null);
  
  // Determine border color based on validation state
  const getBorderColor = () => {
    if (safeError || validationState.isValid === false) {
      return 'border-red-500';
    }
    if (validationState.isWarning) {
      return 'border-yellow-500';
    }
    if (validationState.isValid === true) {
      return 'border-green-500';
    }
    return '';
  };

  // Generate helper text based on validation state
  const getHelperText = () => {
    // If there's an error from parent (form submission), show that
    if (safeError) {
      return null; // Error message is shown separately
    }

    // If validation state has a message, show it
    if (validationState.message) {
      const countryName = validationState.countryName || 
        (COUNTRIES.find(c => c.value === selectedCountry)?.name) || 
        'this country';
      
      if (validationState.isValid === false) {
        // Error state - show error message
        return (
          <p className="text-xs text-red-500 mt-1" role="alert">
            {validationState.message}
          </p>
        );
      } else if (validationState.isWarning) {
        // Warning state - approaching limit
        return (
          <p className="text-xs text-yellow-600 mt-1">
            {validationState.message} - Almost there!
          </p>
        );
      } else if (validationState.maxDigits) {
        // Show digit counter
        return (
          <p className="text-xs text-gray-500 mt-1">
            {validationState.message} ({countryName})
          </p>
        );
      } else {
        // Generic message
        return (
          <p className="text-xs text-gray-500 mt-1">
            {validationState.message}
          </p>
        );
      }
    }

    // Default helper text based on country
    if (selectedCountry !== 'OTHER') {
      const countryData = COUNTRIES.find(c => c.value === selectedCountry);
      const example = countryData?.name === 'Singapore' ? '9000 0000' : 
                     countryData?.name === 'Malaysia' ? '123456789' :
                     countryData?.name === 'India' ? '1234567890' : '1234567890';
      return (
        <p className="text-xs text-gray-500 mt-1">
          Enter phone number without country code (e.g., {example} for {countryData?.name || 'this country'})
        </p>
      );
    } else if (customCountryCode) {
      return (
        <p className="text-xs text-gray-500 mt-1">
          Enter phone number without country code (e.g., 1234567890)
        </p>
      );
    }

    return null;
  };

  // Generate unique ID for aria-describedby
  const inputId = `phone-input-${name}`;
  const helperTextId = `phone-helper-${name}`;

  return (
    <div>
      <label className="form-label flex items-center gap-2 mb-2">
        <Phone className="w-4 h-4 text-green-500" />
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div className="space-y-3">
        <div className="flex gap-2">
          {/* Country Code Dropdown - Compact */}
          <select
            value={selectedCountry}
            onChange={handleCountryChange}
            disabled={disabled}
            className={`select px-2 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-auto min-w-[80px] max-w-[100px] ${
              safeError ? 'border-red-500' : ''
            } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
            aria-label="Country code"
          >
            {COUNTRIES.map(country => (
              <option key={country.value} value={country.value} title={`${country.name} ${country.code}`}>
                {country.code}
              </option>
            ))}
            <option value="OTHER" title="Other country">+?</option>
          </select>
          
          {/* Phone Number Input - Takes majority of space */}
          <input
            type="tel"
            id={inputId}
            name={name}
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder={placeholder}
            disabled={disabled}
            className={`input px-4 rounded-full text-base focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 min-w-0 ${
              getBorderColor()
            } ${disabled || (selectedCountry === 'OTHER' && !customCountryCode) ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
            aria-required={required}
            aria-invalid={!!safeError || validationState.isValid === false}
            aria-describedby={helperTextId}
          />
        </div>
        
        {/* Custom Country Code Input (when Other is selected) */}
        {selectedCountry === 'OTHER' && (
          <div>
            <label className="form-label flex items-center gap-2 mb-2 text-sm">
              <Phone className="w-4 h-4 text-green-500" />
              Country Code
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={customCountryCode}
              onChange={handleCustomCountryCodeChange}
              placeholder="Enter country code (e.g., +1, +44, +33)"
              disabled={disabled}
              className={`input px-4 rounded-full text-base focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                safeError ? 'border-red-500' : ''
              } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
              aria-required="true"
              aria-invalid={!!safeError}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter country code starting with + (e.g., +1 for USA, +44 for UK)
            </p>
          </div>
        )}
      </div>
      {safeError && (
        <p className="form-error mt-1" role="alert" id={`phone-error-${name}`}>{safeError}</p>
      )}
      <div id={helperTextId}>
        {getHelperText()}
      </div>
    </div>
  );
};

export default PhoneNumberInput;
